/**
 * frontend/src/components/PSPModels.js  (Phase 12b)
 *
 * "PSP models" section for a line item: every RAW and cleaned model version a
 * PSP submitted, newest first. The latest RAW shows the review state, the
 * time left before automatic acceptance, and — for admins — Accept / Reject /
 * Hold. Older versions show their outcome (express vs timeout acceptance).
 *
 * Props:
 *   load()                         → Promise<{ data: versions[] }>
 *   downloadUrl(path, name)        → string
 *   onDecide(decision, reason)     → Promise (admin only; omit for read-only)
 *   onPreview(version)             → optional (GLB only)
 *   refreshKey                     → change to reload
 * Renders nothing when the item has no PSP model submissions.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

const STATUS = {
  pending:    { label: 'Pending review', bg: '#FEF3C7', color: '#92400E' },
  on_hold:    { label: 'On hold',        bg: '#E5E7EB', color: '#374151' },
  accepted:   { label: 'Accepted',       bg: '#D1FAE5', color: '#065F46' },
  rejected:   { label: 'Rejected',       bg: '#FEE2E2', color: '#991B1B' },
  superseded: { label: 'Superseded',     bg: '#F3F4F6', color: '#6B7280' },
};

export function timeLeft(deadline, now = Date.now()) {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - now;
  if (ms <= 0) return 'due now';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h >= 1 ? `${h}h ${m}m left` : `${m}m left`;
}

const fmtDT = (d) => (d ? new Date(d).toLocaleString() : '');
const fmtSize = (n) => (n == null ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function PSPModels({ load, downloadUrl, onDecide, onPreview, refreshKey }) {
  const [versions, setVersions] = useState(null);
  const [mode, setMode]         = useState(null);   // 'rejected' | 'on_hold' while typing a reason
  const [reason, setReason]     = useState('');
  const [busy, setBusy]         = useState(false);
  const [, tick]                = useState(0);

  // Loader kept in a ref so an inline `load` prop doesn't refetch every render
  const loadRef = useRef(load);
  loadRef.current = load;
  const reload = useCallback(() => {
    loadRef.current().then(r => setVersions(r.data || [])).catch(() => setVersions([]));
  }, []);
  useEffect(() => { reload(); }, [reload, refreshKey]);
  useEffect(() => { const t = setInterval(() => tick(x => x + 1), 60000); return () => clearInterval(t); }, []); // countdown

  if (!versions || !versions.length) return null;
  const latestRaw = versions.filter(v => v.modelType === 'raw').sort((a, b) => b.version - a.version)[0];
  const canDecide = onDecide && latestRaw && ['pending', 'on_hold'].includes(latestRaw.reviewStatus);

  const decide = async (decision) => {
    setBusy(true);
    try { await onDecide(decision, reason.trim()); setMode(null); setReason(''); }
    catch { /* caller shows the error */ }
    finally { setBusy(false); reload(); }
  };

  const th = { padding: '6px 10px', fontSize: 11, fontWeight: 500, color: '#6b7280', textAlign: 'left', borderBottom: '0.5px solid #e5e7eb' };
  const td = { padding: '6px 10px', fontSize: 12, borderBottom: '0.5px solid #f3f4f6', verticalAlign: 'top' };
  const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' });

  return (
    <div style={{ marginTop: 12, border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '0.5px solid #e5e7eb' }}>
        PSP models
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr><th style={th}>Version</th><th style={th}>Type</th><th style={th}>Status</th><th style={th}>Submitted</th><th style={th}>File</th><th style={th}></th></tr>
          </thead>
          <tbody>
            {versions.map(v => {
              const isLatestRaw = latestRaw && v.id === latestRaw.id;
              const st = v.modelType === 'cleaned' ? null : STATUS[v.reviewStatus];
              const left = isLatestRaw && v.reviewStatus === 'pending' ? timeLeft(v.reviewDeadlineAt) : null;
              return (
                <tr key={v.id} style={{ background: isLatestRaw && canDecide ? '#FFFBEB' : undefined }}>
                  <td style={td}>v{v.version}</td>
                  <td style={td}>{v.modelType === 'raw' ? 'RAW' : 'Cleaned'}</td>
                  <td style={td}>
                    {v.modelType === 'cleaned' ? <span style={{ color: '#6b7280' }}>For reference (no review)</span> : (
                      <>
                        <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: st?.bg, color: st?.color }}>
                          {st?.label}{v.reviewStatus === 'accepted' && v.decisionType ? ` (${v.decisionType === 'timeout' ? 'by timeout' : 'express'})` : ''}
                        </span>
                        {left && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#92400E' }}>⏳ {left}</span>}
                        {v.reviewStatus === 'pending' && isLatestRaw && !v.reviewDeadlineAt && <span style={{ marginLeft: 6, fontSize: 11, color: '#9ca3af' }}>no deadline</span>}
                        {v.reason && <div style={{ fontSize: 11, color: v.reviewStatus === 'rejected' ? '#991B1B' : '#4b5563', marginTop: 2 }}>{v.reason}</div>}
                        {v.decidedAt && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{fmtDT(v.decidedAt)}{v.decidedBy ? ` · ${v.decidedBy}` : ''}</div>}
                      </>
                    )}
                  </td>
                  <td style={{ ...td, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDT(v.submittedAt)}</td>
                  <td style={td}>
                    <a href={downloadUrl(v.path, v.filename)} target="_blank" rel="noreferrer" style={{ color: 'var(--brand, #2563eb)', textDecoration: 'none' }}>
                      ⬇ {v.filename}
                    </a>
                    <span style={{ color: '#9ca3af', marginLeft: 6 }}>{fmtSize(v.fileSize)}</span>
                    {v.sourceUrl && <div style={{ fontSize: 10, color: '#9ca3af', wordBreak: 'break-all' }}>Source: {v.sourceUrl}</div>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {onPreview && /\.glb$/i.test(v.filename) && (
                      <button onClick={() => onPreview(v)} style={{ ...btn('#4b5563'), marginRight: 6 }}>🔄 Preview</button>
                    )}
                    {isLatestRaw && canDecide && !mode && (
                      <>
                        <button disabled={busy} onClick={() => decide('accepted')} style={{ ...btn('#059669'), marginRight: 4 }}>✓ Accept</button>
                        <button disabled={busy} onClick={() => setMode('rejected')} style={{ ...btn('#DC2626'), marginRight: 4 }}>✗ Reject</button>
                        {v.reviewStatus !== 'on_hold' && <button disabled={busy} onClick={() => setMode('on_hold')} style={btn('#6b7280')}>⏸ Hold</button>}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {mode && (
        <div style={{ padding: '10px 12px', borderTop: '0.5px solid #e5e7eb', background: '#FAFAFA' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {mode === 'rejected' ? `Reject RAW v${latestRaw.version} — reason sent to the PSP` : `Put RAW v${latestRaw.version} on hold — stops the review clock (note optional)`}
          </div>
          <textarea className="input" rows={2} value={reason} onChange={e => setReason(e.target.value)} style={{ fontSize: 12, width: '100%', maxWidth: 480 }} />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button disabled={busy || (mode === 'rejected' && !reason.trim())} onClick={() => decide(mode)}
              style={btn(mode === 'rejected' ? '#DC2626' : '#6b7280')}>{mode === 'rejected' ? 'Send rejection' : 'Put on hold'}</button>
            <button onClick={() => { setMode(null); setReason(''); }} className="btn btn-sm" style={{ fontSize: 11 }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
